import { LifebuoyIcon } from "@phosphor-icons/react/dist/csr/Lifebuoy";
import { Link, Navigate, useParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "@volca/ui/components/ui/empty";
import {
  PageContainer,
  PageTitle,
} from "@volca/ui/components/ui/page-components";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { useBreadcrumbs } from "@/app-shell";
import { useGetBusinesses } from "@/business/query";
import { useBusiness } from "@/core/business";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { useGetMyUser } from "@/user/query";
import { HELP_GUIDES } from "./guides/content";
import { PayrollEditsGuide } from "./guides/payroll-edits-guide";
import { WrittenGuide } from "./guides/written-guide";
import { HelpGuideNavigation } from "./help-guide-navigation";
import { getHelpTopicPath, HELP_PATH, LEGACY_HELP_TOPICS } from "./routes";
import {
  getAvailableHelpTopics,
  getFirstHelpGuide,
  getHelpTopicTrail,
} from "./topics";

export default function HelpPage() {
  const { "*": path = "" } = useParams();
  const currentId = path.replace(/\/+$/, "");
  const { isError } = useBusiness();
  const user = useGetMyUser();
  const businesses = useGetBusinesses();
  const { settings, isLoading } = useBusinessProducts();
  const topics = getAvailableHelpTopics(settings);
  const trail = getHelpTopicTrail(topics, currentId);
  const currentTopic = trail.at(-1);
  useBreadcrumbs([
    { label: "Help", href: HELP_PATH },
    ...trail.map((topic, index) => ({
      label: topic.title,
      ...(index < trail.length - 1 && !topic.isSection
        ? { href: getHelpTopicPath(topic.id) }
        : {}),
    })),
  ]);
  const legacyPath = Object.prototype.hasOwnProperty.call(
    LEGACY_HELP_TOPICS,
    currentId,
  )
    ? LEGACY_HELP_TOPICS[currentId]
    : undefined;
  if (legacyPath) return <Navigate to={legacyPath} replace />;
  const firstGuide = !currentId
    ? getFirstHelpGuide(topics)
    : currentTopic?.isSection
      ? getFirstHelpGuide(currentTopic.children ?? [])
      : undefined;
  if (!isLoading && !isError && firstGuide) {
    return <Navigate to={getHelpTopicPath(firstGuide.id)} replace />;
  }

  const retry = () => {
    void user.refetch();
    void businesses.refetch();
  };

  return (
    <PageContainer>
      {isLoading ? (
        <Stack
          justify="center"
          direction="row"
          role="status"
          aria-label="Loading Help"
          className="py-12"
        >
          <Spinner layout="centered" />
        </Stack>
      ) : isError ? (
        <>
          <PageTitle>Help</PageTitle>
          <Stack gap={3} align="start">
            <Text role="alert" tone="destructive">
              Couldn't load Help for this business. Please try again.
            </Text>
            <Button variant="secondary" size="sm" onClick={retry}>
              Try again
            </Button>
          </Stack>
        </>
      ) : !currentId ? (
        <>
          <PageTitle>Help</PageTitle>
          <Empty className={emptyStateVariants({ layout: "panel" })}>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <LifebuoyIcon aria-hidden />
              </EmptyMedia>
              <EmptyDescription>
                No help sections are available for the selected business.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </>
      ) : currentTopic && !currentTopic.isSection ? (
        <article key={currentTopic.id} className="w-full max-w-3xl">
          <PageTitle>{currentTopic.title}</PageTitle>
          {currentTopic.id === "payroll/runs/edits" ? (
            <PayrollEditsGuide />
          ) : HELP_GUIDES[currentTopic.id] ? (
            <WrittenGuide guide={HELP_GUIDES[currentTopic.id]} />
          ) : null}
          <HelpGuideNavigation topics={topics} currentId={currentId} />
        </article>
      ) : (
        <Stack gap={3} align="start">
          <PageTitle>Guide unavailable</PageTitle>
          <Text tone="muted">
            This guide doesn't exist or isn't available for the selected
            business.
          </Text>
          <Link
            to={HELP_PATH}
            className="rounded-md text-sm font-medium text-link underline underline-offset-4 focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none"
          >
            Go to Help
          </Link>
        </Stack>
      )}
    </PageContainer>
  );
}
